/**
 * WebGPU crash memory (pattern from jreb-sailing): a localStorage flag is set *before* the WebGPU
 * attempt and cleared after a few seconds of stable frames. A flag still present at the next boot
 * means the attempt failed, hung or crashed the tab, so `auto` boots WebGL2 immediately.
 * Audit #6 exception: a quick tab-close during the first load leaves the flag behind too, so the
 * first presented frame writes a paint marker; shouldBlockWebGPU() tells "attempted, never
 * painted" (block) apart from "painted, then closed" (retry).
 */
export const CRASH_FLAG_KEY = 'jreb1d.gfx.webgpuAttempt';
/** Audit #6: written on the first presented frame; distinguishes a tab-close artefact from a crash. */
export const WEBGPU_PAINTED_KEY = 'jreb1d.gfx.webgpuPainted';
/** A remembered failure stops blocking WebGPU after this long (e.g. after a browser update). */
export const CRASH_FLAG_MAX_AGE_MS = 3 * 24 * 3600 * 1000;

export interface RememberedGfxFailure {
  at: number;
  /** Why it failed; empty when the attempt never reported back (a hang or a crash). */
  reason: string;
}

export function formatGfxFlag(at: number, reason = ''): string {
  const oneLine = reason.replace(/\s+/g, ' ').trim();
  return oneLine ? `${Math.round(at)} ${oneLine}` : String(Math.round(at));
}

export function parseGfxFlag(value: string | null, now = Date.now()): RememberedGfxFailure | null {
  if (!value) return null;
  const space = value.indexOf(' ');
  const at = Number(space === -1 ? value : value.slice(0, space));
  if (!Number.isFinite(at) || at <= 0) return null;
  if (now - at > CRASH_FLAG_MAX_AGE_MS) return null;
  return { at, reason: space === -1 ? '' : value.slice(space + 1) };
}

export function formatPaintMarker(at: number): string {
  return String(Math.round(at));
}

export function parsePaintMarker(value: string | null): number | null {
  if (!value) return null;
  const at = Number(value);
  return Number.isFinite(at) && at > 0 ? at : null;
}

/**
 * Pure block decision (no localStorage, so it is unit-testable): the attempt flag alone blocks,
 * but a paint marker at-or-after the attempt's `at` means the engine demonstrably presented
 * frames — the leftover flag is a tab-close artefact, not a crash, so do not block. A marker
 * OLDER than the attempt is from an earlier visit: this attempt never painted, keep blocking.
 * The 3-day age rule is applied upstream by parseGfxFlag (an expired attempt arrives as null).
 */
export function shouldBlockWebGPU(attempt: RememberedGfxFailure | null, paintedAtMs: number | null): boolean {
  if (!attempt) return false;
  return paintedAtMs === null || paintedAtMs < attempt.at;
}

export function describeGfxFailure(failure: RememberedGfxFailure): string {
  const when = new Date(failure.at).toLocaleString();
  return failure.reason
    ? `WebGPU failed here on ${when} (${failure.reason}); retry blocked for ${Math.round(CRASH_FLAG_MAX_AGE_MS / 3600000)} h`
    : `WebGPU hung or crashed here on ${when}; retry blocked for ${Math.round(CRASH_FLAG_MAX_AGE_MS / 3600000)} h`;
}
